import { Component } from "../lib/component.ts";
import { measureLightness, srgbToLstar } from "../lib/image-lightness.ts";

interface ModalPanelLike extends HTMLElement {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  refs: { content?: HTMLElement };
}

interface HeaderRefs {
  toggleButtons?: HTMLButtonElement[];
}

interface VideoSource {
  url: string;
  mime_type: string;
}

function hasProperty<K extends string>(
  value: object,
  key: K,
): value is Record<K, unknown> & object {
  return key in value;
}

function isModalPanelLike(element: Element | null): element is ModalPanelLike {
  if (!(element instanceof HTMLElement)) return false;
  if (!hasProperty(element, "isOpen") || typeof element.isOpen !== "boolean") return false;
  if (!hasProperty(element, "open") || typeof element.open !== "function") return false;
  if (!hasProperty(element, "close") || typeof element.close !== "function") return false;
  return true;
}

function isVideoSourceArray(value: unknown): value is VideoSource[] {
  if (!Array.isArray(value)) return false;
  return value.every((entry: unknown) => {
    if (entry === null || typeof entry !== "object") return false;
    if (!hasProperty(entry, "url") || typeof entry.url !== "string") return false;
    if (!hasProperty(entry, "mime_type") || typeof entry.mime_type !== "string") return false;
    return true;
  });
}

/**
 * Header component with flyout menu functionality.
 * Delegates overlay behavior (scroll lock, backdrop, inert, focus, escape)
 * to a sibling `<modal-panel>`.
 */
class SiteHeader extends Component<HeaderRefs> {
  #panel: ModalPanelLike | null = null;

  #videoContainer: HTMLElement | null = null;

  #videoPauseControllers: WeakMap<HTMLVideoElement, AbortController> = new WeakMap();

  #videoCache: Map<string, HTMLVideoElement> = new Map();

  #imageCache: Map<string, HTMLImageElement> = new Map();

  #menuListenersAttached = false;

  #mediaObserver: IntersectionObserver | null = null;

  #intersectingMedia: Set<Element> = new Set();

  #resizeTimeout = 0;

  #scrollRAF = 0;

  #colorGeneration = 0;

  #backgroundL = 100;

  connectedCallback(): void {
    super.connectedCallback();

    const panelId = this.refs.toggleButtons?.[0]?.getAttribute("aria-controls");
    const panelEl = panelId ? document.getElementById(panelId) : null;
    this.#panel = isModalPanelLike(panelEl) ? panelEl : null;
    this.#videoContainer = document.getElementById("video-overlay");

    for (const button of this.refs.toggleButtons ?? []) {
      button.addEventListener("click", this.#handleToggle, { signal: this.signal });
    }

    this.#panel?.addEventListener("open", this.#handleOpen, { signal: this.signal });
    this.#panel?.addEventListener("close", this.#handleClose, { signal: this.signal });

    document.addEventListener("shopify:section:select", this.#handleSectionSelect, {
      signal: this.signal,
    });

    document.addEventListener("shopify:section:deselect", this.#handleSectionDeselect, {
      signal: this.signal,
    });

    this.#createMediaObserver();

    document.addEventListener("scroll", this.#handleScroll, {
      signal: this.signal,
      capture: true,
      passive: true,
    });

    window.addEventListener("resize", this.#handleResize, { signal: this.signal });

    // Publish the real header height for layouts that start below the fixed
    // header (the product page's gallery and sticky buy box). The CSS token
    // in _variables.scss is the fallback before this runs.
    const heightObserver = new ResizeObserver(() => {
      document.documentElement.style.setProperty("--header-height", `${this.offsetHeight}px`);
    });
    heightObserver.observe(this);
    this.signal.addEventListener("abort", () => {
      heightObserver.disconnect();
      document.documentElement.style.removeProperty("--header-height");
    });

    document.addEventListener("shopify:section:load", this.#handleSectionChange, {
      signal: this.signal,
    });
    document.addEventListener("shopify:section:unload", this.#handleSectionChange, {
      signal: this.signal,
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#mediaObserver?.disconnect();
    this.#mediaObserver = null;
    this.#intersectingMedia.clear();
    window.clearTimeout(this.#resizeTimeout);
    cancelAnimationFrame(this.#scrollRAF);
  }

  #handleToggle = (): void => {
    if (!this.#panel) return;

    if (this.#panel.isOpen) {
      this.#setAriaExpanded(false);
      this.#panel.close();
    } else {
      this.#setOffset();
      this.#panel.open();
    }
  };

  #handleOpen = (): void => {
    this.#setAriaExpanded(true);
    this.#attachMenuListeners();
    this.classList.add("header--opaque");
    this.classList.remove("header--light");
  };

  #handleClose = (): void => {
    if (this.#panel?.isOpen) return;
    this.#setAriaExpanded(false);
    this.classList.remove("header--opaque");

    // Safari 26 Liquid Glass: Safari caches the toolbar tint sampled from
    // fixed elements and won't resample when a CSS class changes the
    // computed background-color. Toggling display: none forces a resample,
    // but only if the compositor paints a frame without the element — a
    // double rAF ensures that.
    this.style.display = "none";
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.style.removeProperty("display");
        this.#updateHeaderColor();
      });
    });
  };

  #setOffset(): void {
    const rect = this.getBoundingClientRect();
    this.#panel?.style.setProperty("--modal-panel-offset", `${rect.top + rect.height}px`);
  }

  #setAriaExpanded(expanded: boolean): void {
    for (const button of this.refs.toggleButtons ?? []) {
      button.setAttribute("aria-expanded", String(expanded));
    }
  }

  #attachMenuListeners(): void {
    if (this.#menuListenersAttached) return;
    this.#menuListenersAttached = true;

    const content = this.#panel?.refs?.content;
    if (!content) return;

    for (const link of content.querySelectorAll("[ref='menuLinks[]']")) {
      link.addEventListener("mouseenter", this.#handleLinkHover, { signal: this.signal });
      link.addEventListener("mouseleave", this.#handleLinkLeave, { signal: this.signal });
      link.addEventListener("focus", this.#handleLinkHover, { signal: this.signal });
      link.addEventListener("blur", this.#handleLinkLeave, { signal: this.signal });
    }
  }

  #getOrCreateImage(linkId: string, dataset: DOMStringMap): HTMLImageElement {
    const cached = this.#imageCache.get(linkId);
    if (cached) return cached;

    const img = new Image();
    img.className = "flyout__collection-image";
    img.src = dataset.imageUrl ?? "";
    img.alt = dataset.imageAlt ?? "";
    img.sizes = "25vw";
    if (dataset.imageSrcset) img.srcset = dataset.imageSrcset;
    img.dataset.link = linkId;

    const container = this.#panel?.refs?.content?.querySelector("[ref='imageContainer']");
    container?.appendChild(img);
    this.#imageCache.set(linkId, img);
    return img;
  }

  #getOrCreateVideo(linkId: string, sourcesJson: string): HTMLVideoElement {
    const cached = this.#videoCache.get(linkId);
    if (cached) return cached;

    const video = document.createElement("video");
    video.className = "video-overlay__video";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.dataset.link = linkId;

    const parsed: unknown = JSON.parse(sourcesJson);
    if (isVideoSourceArray(parsed)) {
      for (const source of parsed) {
        const el = document.createElement("source");
        el.src = source.url;
        el.type = source.mime_type;
        video.appendChild(el);
      }
    }

    this.#videoContainer?.appendChild(video);
    this.#videoCache.set(linkId, video);
    return video;
  }

  #handleLinkHover = (event: Event): void => {
    if (!(event.target instanceof HTMLElement)) return;
    const linkId = event.target.dataset.link;
    if (!linkId) return;

    const { imageUrl, videoSources } = event.target.dataset;

    if (imageUrl) {
      const img = this.#getOrCreateImage(linkId, event.target.dataset);
      img.classList.add("flyout__collection-image--active");
    }

    if (videoSources) {
      const video = this.#getOrCreateVideo(linkId, videoSources);
      this.#videoPauseControllers.get(video)?.abort();
      video.classList.add("video-overlay__video--active");
      video.play();
    }
  };

  #handleLinkLeave = (event: Event): void => {
    if (!(event.target instanceof HTMLElement)) return;
    const linkId = event.target.dataset.link;
    if (!linkId) return;

    const img = this.#imageCache.get(linkId);
    if (img) {
      img.classList.remove("flyout__collection-image--active");
    }

    const video = this.#videoCache.get(linkId);
    if (video) {
      video.classList.remove("video-overlay__video--active");
      const controller = new AbortController();
      this.#videoPauseControllers.set(video, controller);
      video.addEventListener("transitionend", () => video.pause(), {
        once: true,
        signal: controller.signal,
      });
    }
  };

  #createMediaObserver(): void {
    this.#mediaObserver?.disconnect();
    this.#intersectingMedia.clear();
    this.#backgroundL = this.#resolveBackgroundL();

    const headerHeight = this.getBoundingClientRect().height;
    // A hidden or collapsed window can report a viewport shorter than the
    // header; a negative clip would produce "--Npx" and throw.
    const bottomClip = Math.max(0, window.innerHeight - headerHeight / 2);

    this.#mediaObserver = new IntersectionObserver(this.#handleMediaIntersect, {
      rootMargin: `0px 0px -${bottomClip}px 0px`,
      threshold: 0,
    });

    const headerGroup = this.closest(".shopify-section-group-header-group");
    let sibling: Element | null =
      headerGroup?.nextElementSibling ??
      this.closest(".shopify-section")?.nextElementSibling ??
      null;
    while (sibling) {
      const sections: Iterable<Element> = sibling.classList.contains("shopify-section")
        ? [sibling]
        : sibling.querySelectorAll(".shopify-section");
      for (const section of sections) {
        for (const img of section.querySelectorAll(
          "img:not([data-header-ignore]):not([data-header-ignore] *)",
        )) {
          this.#mediaObserver.observe(img);
        }
        for (const video of section.querySelectorAll(
          "video[poster]:not([data-header-ignore]):not([data-header-ignore] *)",
        )) {
          this.#mediaObserver.observe(video);
        }
      }
      sibling = sibling.nextElementSibling;
    }
  }

  #handleMediaIntersect = (entries: IntersectionObserverEntry[]): void => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.#intersectingMedia.add(entry.target);
      } else {
        this.#intersectingMedia.delete(entry.target);
      }
    }
    this.#updateHeaderColor();
  };

  async #updateHeaderColor(): Promise<void> {
    if (this.classList.contains("header--opaque")) return;
    if (window.scrollY < 0) return;

    const generation = ++this.#colorGeneration;
    const vw = window.innerWidth;

    const candidates: { src: string; visibleWidth: number }[] = [];

    for (const media of this.#intersectingMedia) {
      if (!(media instanceof HTMLElement)) continue;
      if (!media.checkVisibility({ opacityProperty: true, visibilityProperty: true })) continue;

      let src: string | null = null;
      if (media instanceof HTMLVideoElement) {
        src = media.poster;
      } else if (media instanceof HTMLImageElement) {
        src = media.currentSrc || media.getAttribute("src");
      } else {
        src = media.getAttribute("src");
      }
      if (!src) continue;

      const rect = media.getBoundingClientRect();
      const visibleWidth = Math.min(rect.right, vw) - Math.max(rect.left, 0);
      if (visibleWidth <= 0) continue;

      candidates.push({ src, visibleWidth });
    }

    if (candidates.length === 0) {
      if (this.classList.contains("header--ready") && !this.classList.contains("header--light"))
        return;
      this.classList.remove("header--light");
      requestAnimationFrame(() => this.classList.add("header--ready"));
      return;
    }

    const results = await Promise.allSettled(
      candidates.map(async ({ src, visibleWidth }) => ({
        lstar: await measureLightness(src),
        visibleWidth,
      })),
    );
    if (generation !== this.#colorGeneration) return;

    let weightedL = 0;
    let coveredWidth = 0;
    for (const result of results) {
      if (result.status !== "fulfilled") continue;
      const { lstar, visibleWidth } = result.value;
      weightedL += lstar * visibleWidth;
      coveredWidth += visibleWidth;
    }

    if (coveredWidth === 0) {
      if (this.classList.contains("header--ready") && !this.classList.contains("header--light"))
        return;
      this.classList.remove("header--light");
      requestAnimationFrame(() => this.classList.add("header--ready"));
      return;
    }

    const uncoveredWidth = Math.max(0, vw - coveredWidth);
    weightedL += uncoveredWidth * this.#backgroundL;
    const averageL = weightedL / (coveredWidth + uncoveredWidth);

    this.classList.toggle("header--light", averageL < 65);
    requestAnimationFrame(() => this.classList.add("header--ready"));
  }

  #resolveBackgroundL(): number {
    const el = document.createElement("div");
    el.style.backgroundColor = "var(--background-color)";
    document.body.appendChild(el);
    const raw = getComputedStyle(el).backgroundColor;
    el.remove();
    const m = raw.match(/[\d.]+/g);
    return m ? srgbToLstar(+m[0], +m[1], +m[2]) : 100;
  }

  #handleScroll = (): void => {
    if (this.#scrollRAF) return;
    this.#scrollRAF = requestAnimationFrame(() => {
      this.#scrollRAF = 0;
      this.#updateHeaderColor();
    });
  };

  #handleResize = (): void => {
    window.clearTimeout(this.#resizeTimeout);
    this.#resizeTimeout = window.setTimeout(() => this.#createMediaObserver(), 150);
  };

  #handleSectionChange = (): void => {
    this.#createMediaObserver();
  };

  #handleSectionSelect = (event: Event): void => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.#setOffset();
      this.#panel?.open();
    }
  };

  #handleSectionDeselect = (event: Event): void => {
    if (event.target instanceof Element && event.target.contains(this)) {
      this.#panel?.close();
    }
  };
}

if (!customElements.get("site-header")) {
  customElements.define("site-header", SiteHeader);
}
